import _ from 'lodash';
import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, Icon, IconTooltip } from '~/components';
import { colors, weights } from '~/styles';
import { CellValue } from '../Table';
import { useConversation } from '../ConversationContext';

// Generic confirmation renderer for "write" tools. Each pending record renders as a card of label/value
// pairs (values via the table's CellValue, so they read identically everywhere) plus an action button that
// posts a pre-baked message back into the thread — the model then performs the write; the form never
// mutates. A specialized renderer (e.g. TrackTimePreflight) reuses it the way a specialized pivot reuses
// Pivot, supplying the recordset fields/data plus props: title, confirmLabel, confirmMessage,
// canConfirm (value or fn(data)), omitFields, itemLabel (noun for "Show all N …"), statusFor, noteFor, labelFor.
// Long lists collapse to the first card with a toggle; the action always covers every entry, not just the
// visible one.
const PREVIEW_CARDS = 1;

const S = {
  Container: styled.div`
    margin: 0.75rem 0;
  `,

  Title: styled.div`
    font-size: 0.875rem;
    font-weight: ${weights.bold};
    color: ${colors.black};
    margin-bottom: 0.5rem;
  `,

  Card: styled.div`
    position: relative;
    border: 1px solid ${colors.grey10};
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;

    & + & {
      margin-top: 0.5rem;
    }
  `,

  Status: styled.div`
    position: absolute;
    top: 0.625rem;
    right: 0.75rem;
  `,

  Grid: styled.div`
    display: grid;
    grid-template-columns: minmax(8rem, max-content) 1fr;
    column-gap: 1rem;
    row-gap: 0.375rem;
    font-size: 0.8125rem;
    align-items: baseline;
    /* room for the corner status icon so a long value never runs under it */
    padding-right: 1.5rem;
  `,

  Label: styled.div`
    color: ${colors.grey75};
    font-weight: ${weights.bold};
  `,

  Value: styled.div`
    color: ${colors.black};
  `,

  ValueList: styled.ul`
    margin: 0;
    padding-left: 1rem;
    list-style: disc;

    li + li {
      margin-top: 0.25rem;
    }
  `,

  Note: styled.div`
    display: flex;
    align-items: baseline;
    gap: 0.375rem;
    margin-top: 0.5rem;
    font-size: 0.75rem;
    color: ${colors.warning100};
  `,

  // Animated reveal of the collapsed-away cards. Transitioning grid-template-rows 0fr->1fr lets the height
  // ease open/closed without measuring it; opacity adds a gentle fade. Falls back to an instant toggle
  // where the fr-unit transition or reduced motion is in effect.
  Collapsible: styled.div`
    display: grid;
    grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    transition:
      grid-template-rows 0.3s ease,
      opacity 0.3s ease;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `,

  CollapsibleInner: styled.div`
    overflow: hidden;
    min-height: 0;

    /* Separate the first revealed card from the preview card above (other cards space via Card's own rule). */
    > div:first-child {
      margin-top: 0.5rem;
    }
  `,

  ToggleRow: styled.div`
    display: flex;
    justify-content: center;
    margin-top: 0.5rem;
  `,

  // Pill toggle matching the result table's; overrides the global <button> styling.
  Toggle: styled.button`
    border: 1px solid ${colors.grey10};
    border-radius: 999rem;
    background: ${colors.white};
    color: ${colors.grey75};
    font-size: 0.75rem;
    font-weight: ${weights.normal};
    height: auto;
    padding: 0.375rem 0.75rem;
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

    &:hover {
      background: ${colors.grey5};
      color: ${colors.black};
    }
  `,

  Actions: styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 0.75rem;
  `,
};

export default function ConfirmationForm({
  data,
  fields,
  isLast,
  title,
  confirmLabel = 'Confirm',
  confirmMessage = 'Confirmed — please proceed.',
  canConfirm = true,
  omitFields = [],
  itemLabel,
  statusFor,
  noteFor,
  labelFor,
}) {
  const conversation = useConversation();
  const onSend = conversation?.onSend;
  const [isExpanded, setIsExpanded] = useState(false);

  if (!Array.isArray(data) || data.length === 0 || !Array.isArray(fields)) return null;

  // canConfirm / confirmMessage may be a value or a function of the data (e.g. count the "ready" rows).
  const allowConfirm = typeof canConfirm === 'function' ? canConfirm(data) : canConfirm;
  const message = typeof confirmMessage === 'function' ? confirmMessage(data) : confirmMessage;

  const visibleFields = fields.filter((field) => field.visible !== false && !omitFields.includes(field.key));
  // Only offer the action on the latest turn — a button on a scrolled-up, already-confirmed form would
  // re-submit the same write.
  const showAction = isLast && typeof onSend === 'function' && allowConfirm;

  const canCollapse = data.length > PREVIEW_CARDS;

  // An array value is a list of messages (e.g. the per-condition issues on a time entry): bulleted when
  // there are several, plain text for one. Scalars render through the table's CellValue as before.
  const renderValue = (value, field, row) => {
    if (!Array.isArray(value)) return <CellValue value={value} field={field} row={row} />;
    if (value.length === 1) return value[0];
    return (
      <S.ValueList>
        {value.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </S.ValueList>
    );
  };

  // One card. Kept as a closure so the preview cards and the collapsible ones render identically.
  const renderCard = (row, index) => {
    // Skip empty fields so a card shows only what's actually set on this record.
    const pairs = visibleFields.filter((field) => {
      const value = _.get(row, field.path);
      return value != null && value !== '' && !(Array.isArray(value) && value.length === 0);
    });
    const status = statusFor?.(row);
    const note = noteFor?.(row);
    return (
      <S.Card key={index}>
        {status && (
          <S.Status>
            <IconTooltip icon={status.icon} color={status.color} message={status.label} />
          </S.Status>
        )}
        <S.Grid>
          {pairs.map((field) => (
            <React.Fragment key={field.key}>
              <S.Label>{labelFor ? labelFor(field, row) : field.label}</S.Label>
              <S.Value>{renderValue(_.get(row, field.path), field, row)}</S.Value>
            </React.Fragment>
          ))}
        </S.Grid>
        {note && (
          <S.Note>
            <Icon icon="exclamation-triangle" color={colors.warning} />
            <span>{note}</span>
          </S.Note>
        )}
      </S.Card>
    );
  };

  return (
    <S.Container>
      {title && <S.Title>{title}</S.Title>}

      {data.slice(0, PREVIEW_CARDS).map((row, index) => renderCard(row, index))}

      {canCollapse && (
        <S.Collapsible $open={isExpanded} aria-hidden={!isExpanded}>
          <S.CollapsibleInner>
            {data.slice(PREVIEW_CARDS).map((row, index) => renderCard(row, index + PREVIEW_CARDS))}
          </S.CollapsibleInner>
        </S.Collapsible>
      )}

      {canCollapse && (
        <S.ToggleRow>
          <S.Toggle type="button" onClick={() => setIsExpanded((expanded) => !expanded)}>
            {isExpanded ? 'Show fewer' : `Show all ${data.length}${itemLabel ? ` ${itemLabel}` : ''}`}
          </S.Toggle>
        </S.ToggleRow>
      )}

      {showAction && (
        <S.Actions>
          <Button onClick={() => onSend(message)}>{confirmLabel}</Button>
        </S.Actions>
      )}
    </S.Container>
  );
}
